import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ModuleHeader from "../components/shared/ModuleHeader";
import { useToast } from "../components/shared/Toast";
import { importCandidatesApi } from "../services/api";

const HIDDEN_PROVIDERS = new Set(["manual", "file_upload", "image_upload"]);

const PROVIDER_META = {
  gmail: { icon: "G", family: "Google", purpose: "Bills, renewals, receipts, warranties, and confirmations" },
  google_calendar: { icon: "31", family: "Google", purpose: "Trips, appointments, events, and household commitments" },
  google_drive: { icon: "△", family: "Google", purpose: "Policies, manuals, warranties, invoices, and household documents" },
  outlook: { icon: "O", family: "Microsoft", purpose: "Bills, renewals, receipts, warranties, and confirmations" },
  outlook_calendar: { icon: "31", family: "Microsoft", purpose: "Trips, appointments, events, and household commitments" },
  forwarding: { icon: "↗", family: "Email", purpose: "A private address for forwarded household messages" },
  other_email: { icon: "@", family: "Email", purpose: "Yahoo, iCloud, Fastmail, or another IMAP inbox" },
};

function dateTimeLabel(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(date);
}

function connectionState(source) {
  if (source.permission_status === "connected") {
    if (source.last_sync_status === "error") return { label: "Needs attention", tone: "danger" };
    if (source.last_sync_status === "success") return { label: "Sync healthy", tone: "healthy" };
    return { label: "Connected", tone: "connected" };
  }
  if (source.permission_status === "pending_user_setup") return { label: "Finish setup", tone: "warning" };
  if (source.status === "disabled") return { label: "Turned off", tone: "muted" };
  return { label: "Not connected", tone: "muted" };
}

function scanSummary(source) {
  const cursor = source.sync_cursor || {};
  const checked = Number(cursor.adapter_checked ?? cursor.checked ?? 0);
  const created = Number(cursor.adapter_created ?? cursor.created ?? 0);
  if (!source.last_sync_attempt_at && !source.last_synced_at) return "No scans yet";
  return `${checked} checked · ${created} sent to review`;
}

function ConnectionCard({ source, busy, onAction, focused = false }) {
  const meta = PROVIDER_META[source.provider] || { icon: "◇", family: "Connection", purpose: source.access_level };
  const state = connectionState(source);
  const connected = source.permission_status === "connected";
  const oauth = ["oauth", "calendar_oauth", "document_oauth"].includes(source.source_type);
  const canConnect = !connected;
  return (
    <article id={`connection-${source.id}`} className={`hm-connection-card is-${state.tone}${focused ? " is-focused" : ""}`}>
      <div className="hm-connection-card__icon" aria-hidden="true">{meta.icon}</div>
      <div className="hm-connection-card__title">
        <span>{meta.family}</span>
        <h2>{source.label.replace(" connector", "")}</h2>
        <p>{meta.purpose}</p>
      </div>
      <strong className={`hm-connection-card__state is-${state.tone}`}>{state.label}</strong>

      <div className="hm-connection-card__facts">
        <div><span>Account</span><b>{source.source_account || (connected ? "Connected account" : "None")}</b></div>
        <div><span>Last successful sync</span><b>{dateTimeLabel(source.last_synced_at)}</b></div>
        <div><span>Latest scan</span><b>{scanSummary(source)}</b></div>
      </div>

      {source.last_sync_error && (
        <div className="hm-connection-card__error"><b>HouseMind could not complete the latest scan.</b><span>{source.last_sync_error}</span></div>
      )}
      {source.provider === "forwarding" && source.source_account && (
        <div className="hm-connection-card__address"><span>Your forwarding address</span><b>{source.source_account}</b><button type="button" onClick={() => navigator.clipboard?.writeText(source.source_account)}>Copy</button></div>
      )}

      <div className="hm-connection-card__actions">
        {connected && <button type="button" className="is-primary" disabled={busy} onClick={() => onAction(source, "sync")}>{busy ? "Checking…" : "Sync now"}</button>}
        {connected && source.provider === "gmail" && <button type="button" disabled={busy} onClick={() => onAction(source, "recheck")}>Recheck 30 days</button>}
        {canConnect && <button type="button" className="is-primary" disabled={busy} onClick={() => onAction(source, "connect")}>{busy ? "Opening…" : source.status === "disabled" ? "Turn on" : source.provider === "forwarding" ? "Create address" : oauth ? "Connect" : "Set up IMAP"}</button>}
        {connected && oauth && <button type="button" disabled={busy} onClick={() => onAction(source, "connect")}>Change account</button>}
        {connected && <button type="button" className="is-danger" disabled={busy} onClick={() => onAction(source, "disconnect")}>Disconnect</button>}
      </div>
    </article>
  );
}

export default function ConnectionsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [syncAllBusy, setSyncAllBusy] = useState(false);
  const focusedSourceId = Number(searchParams.get("source") || 0) || null;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setPayload(await importCandidatesApi.sources()); }
    catch (err) { setError(err?.message || "HouseMind could not load your connections."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchParams.get("connector") !== "setup") return;
    const status = searchParams.get("status");
    const connectionId = Number(searchParams.get("connection_id"));
    const shouldRunFirstSync = status === "connected" && searchParams.get("first_sync") === "1" && connectionId > 0;
    toast(
      status === "connected" ? (shouldRunFirstSync ? "Connection complete. HouseMind is running the first scan…" : "Connection complete. HouseMind is ready to scan this source.") : "Connection setup did not complete. Try again from the connection card.",
      status === "connected" ? "success" : "error",
    );
    const next = new URLSearchParams(searchParams);
    next.delete("connector"); next.delete("status"); next.delete("connection_id"); next.delete("first_sync");
    setSearchParams(next, { replace: true });
    if (shouldRunFirstSync) {
      setBusyId(connectionId);
      importCandidatesApi.syncConnection(connectionId).then(result => {
        const stats = result?.stats || {};
        toast(`First scan complete: ${Number(stats.adapter_checked || 0)} checked, ${Number(stats.adapter_created || 0)} sent to Discovery review.`);
      }).catch(error => toast(error?.message || "Connected successfully, but the first scan needs a retry.", "error")).finally(() => {
        setBusyId(null);
        load();
      });
    } else load();
  }, [load, searchParams, setSearchParams, toast]);

  const sources = useMemo(() => (payload?.sources || []).filter(row => !HIDDEN_PROVIDERS.has(row.provider)), [payload]);
  useEffect(() => {
    if (!focusedSourceId || loading) return undefined;
    const timer = window.setTimeout(() => document.getElementById(`connection-${focusedSourceId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    return () => window.clearTimeout(timer);
  }, [focusedSourceId, loading]);
  const summary = useMemo(() => ({
    connected: sources.filter(row => row.permission_status === "connected").length,
    healthy: sources.filter(row => row.permission_status === "connected" && row.last_sync_status !== "error").length,
    attention: sources.filter(row => row.last_sync_status === "error" || row.permission_status === "pending_user_setup").length,
  }), [sources]);

  const act = useCallback(async (source, action) => {
    if (!source?.id) return;
    if (action === "disconnect" && !window.confirm(`Disconnect ${source.label}? HouseMind will stop scanning it, but previously confirmed records stay saved.`)) return;
    setBusyId(source.id); setError("");
    try {
      if (action === "connect") {
        if (source.provider === "other_email") {
          navigate("/app?review=sources");
          return;
        }
        const result = await importCandidatesApi.requestConnection(source.id, { source_account: source.source_account || "" });
        if (result?.oauth?.ready && result.oauth.auth_url) { window.location.assign(result.oauth.auth_url); return; }
        toast(result?.oauth?.message || `${source.label} setup is ready.`);
      } else if (action === "disconnect") {
        await importCandidatesApi.disconnectConnection(source.id);
        toast(`${source.label} disconnected.`);
      } else {
        const result = await importCandidatesApi.syncConnection(source.id, { recheck: action === "recheck" });
        const stats = result?.stats || {};
        toast(`${source.label}: ${Number(stats.adapter_checked || 0)} checked, ${Number(stats.adapter_created || 0)} new review items.`);
      }
      await load();
    } catch (err) { setError(err?.message || `HouseMind could not update ${source.label}.`); }
    finally { setBusyId(null); }
  }, [load, navigate, toast]);

  const syncAll = useCallback(async () => {
    setSyncAllBusy(true); setError("");
    try {
      const result = await importCandidatesApi.syncAllConnections();
      const stats = result?.stats || {};
      toast(`HouseMind checked all connected sources: ${Number(stats.adapter_checked || 0)} items checked and ${Number(stats.adapter_created || 0)} new review items found.`);
      await load();
    } catch (err) { setError(err?.message || "HouseMind could not scan all connected sources."); }
    finally { setSyncAllBusy(false); }
  }, [load, toast]);

  return (
    <main className="hm-connections-page">
      <ModuleHeader eyebrow="HouseMind integrations" title="Connections" subtitle="See what HouseMind can discover, when it last checked, and what needs your attention." />
      <section className="hm-connections-overview">
        <div><span>Discovery coverage</span><h1>{summary.connected} connected source{summary.connected === 1 ? "" : "s"}</h1><p>Connected sources are read-only. New information still goes to Discovery review before it changes a module.</p></div>
        <div className="hm-connections-overview__stats">
          <article><strong>{summary.healthy}</strong><span>Healthy</span></article>
          <article className={summary.attention ? "has-attention" : ""}><strong>{summary.attention}</strong><span>Needs attention</span></article>
          <article><strong>{sources.length}</strong><span>Available</span></article>
        </div>
        <div className="hm-connections-overview__actions"><button type="button" className="is-primary" onClick={syncAll} disabled={syncAllBusy || summary.connected === 0}>{syncAllBusy ? "Scanning all…" : "Scan all connected sources"}</button><button type="button" onClick={load} disabled={loading}>{loading ? "Checking…" : "Refresh health"}</button></div>
      </section>
      {error && <div className="hm-connections-error" role="alert">{error}</div>}
      {loading && !payload ? <div className="hm-connections-loading">Checking connection health…</div> : (
        <section className="hm-connections-list">
          {sources.map(source => <ConnectionCard key={source.id} source={source} busy={busyId === source.id} onAction={act} focused={focusedSourceId === source.id} />)}
        </section>
      )}
      <section className="hm-connections-privacy"><span>🔒 Read-only by design</span><h2>You stay in control.</h2><p>HouseMind stores sync health and discovered review items—not an unrestricted copy of your inbox. Disconnecting stops future scans without deleting records you already approved.</p></section>
    </main>
  );
}
